import React, { useEffect, useState } from 'react'
import { Box, ButtonBase, Paper, Stack, Typography } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import {
  countEvents,
  formatClock,
  getArrestPrompts,
  getClocks,
  getCompressionFraction,
  getCycleState,
  lastEvent,
} from './engine'
import { toneOf } from './liveUi'

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.45; }
  100% { opacity: 1; }
`

// The only component on the console with a 1-second tick, so the action
// grid doesn't re-render every second.
export const useNow = (intervalMs = 1000) => {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])
  return now
}

const Stat = ({
  label,
  value,
  sub,
  onClick,
  tone = 'graphite',
  alert = false,
  big = false,
}) => {
  const colours = toneOf(tone)
  return (
    <Paper
      component={onClick ? ButtonBase : 'div'}
      onClick={onClick}
      sx={{
        display: 'block',
        textAlign: 'left',
        px: 1.5,
        py: 1,
        borderRadius: 2,
        minWidth: { xs: 'calc(50% - 4px)', sm: 140 },
        flex: big ? '1 1 220px' : '1 1 140px',
        bgcolor: alert ? colours.main : colours.bg,
        color: alert ? '#fff' : colours.dark,
        animation: alert ? `${pulse} 1s ease-in-out infinite` : undefined,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ textTransform: 'uppercase', opacity: 0.85 }}
      >
        {label}
      </Typography>
      <Typography
        fontFamily="monospace"
        fontWeight={800}
        fontSize={
          big ? { xs: '2.2rem', md: '2.8rem' } : { xs: '1.5rem', md: '1.8rem' }
        }
        lineHeight={1.1}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" fontWeight={700} sx={{ opacity: 0.85 }} noWrap>
          {sub}
        </Typography>
      )}
    </Paper>
  )
}

const SHOCK_TYPES = ['shock', 'dsed', 'vectorChange', 'Shock']
const DRUG_TYPES = ['arrestDrug', 'Drug']

// Arrest timers (elapsed, cycle, since drug, shocks, CCF, cognitive-aid
// prompts) when `showArrest`, plus the running clocks row.
const ConsoleStatus = ({
  record,
  mergedEvents,
  clockKeys,
  showArrest,
  phase,
  level,
  cycleLengthSeconds,
  onRhythmCheck,
}) => {
  const now = useNow(1000)
  const log = record?.cardiacArrest
  const arrestEvents = log?.events || []

  const clocks = getClocks(mergedEvents, clockKeys, now).filter(
    // the arrest header already shows time since the last drug
    (clock) => !(showArrest && clock.key === 'arrestDrug'),
  )

  let arrest = null
  if (showArrest && log?.startedAt) {
    // Once resuscitation has ended the arrest clock stops at ROLE / end
    const ended =
      phase === 'ended' ? lastEvent(arrestEvents, ['role', 'terminated']) : null
    const until = ended ? new Date(ended.time).getTime() : now
    const elapsed = Math.max(
      0,
      Math.floor((until - new Date(log.startedAt).getTime()) / 1000),
    )
    const cycle = getCycleState(log, cycleLengthSeconds, now)
    const lastDrug = lastEvent(arrestEvents, DRUG_TYPES)
    const sinceDrug = lastDrug
      ? Math.floor((now - new Date(lastDrug.time).getTime()) / 1000)
      : null
    const shocks = SHOCK_TYPES.reduce(
      (total, type) => total + countEvents(arrestEvents, type),
      0,
    )
    const ccf = getCompressionFraction(log, now)
    const prompts = getArrestPrompts(record, { level, now })
    const lastDrugName = lastDrug?.data?.drug?.name || lastDrug?.detail || null
    arrest = { elapsed, cycle, sinceDrug, lastDrugName, shocks, ccf, prompts }
  }

  if (!arrest && !clocks.length) return null

  return (
    <Stack gap={1}>
      {arrest && (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Stat
            label="Arrest time"
            value={formatClock(arrest.elapsed)}
            tone="red"
            big
          />
          {phase === 'arrest' && (
            <Stat
              label={arrest.cycle.overdue ? 'Rhythm check due' : 'Next rhythm check'}
              value={formatClock(arrest.cycle.remainingSeconds)}
              // The countdown is also the button that logs the rhythm check
              onClick={onRhythmCheck}
              sub={
                onRhythmCheck
                  ? arrest.cycle.overdue
                    ? 'Tap to log rhythm check'
                    : 'Tap to log early'
                  : undefined
              }
              tone="red"
              alert={arrest.cycle.overdue}
              big
            />
          )}
          <Stat
            label="Last drug"
            value={
              arrest.sinceDrug === null
                ? 'None'
                : `${formatClock(arrest.sinceDrug)} ago`
            }
            sub={arrest.lastDrugName}
            tone="green"
            alert={
              phase === 'arrest' &&
              arrest.sinceDrug !== null &&
              arrest.sinceDrug >= 240
            }
          />
          <Stat label="Shocks" value={arrest.shocks} tone="amber" />
          {arrest.ccf !== null && (
            <Stat
              label="Compression fraction"
              value={`${arrest.ccf}%`}
              tone={arrest.ccf < 80 ? 'amber' : 'green'}
            />
          )}
        </Stack>
      )}
      {arrest?.prompts?.map((prompt) => {
        const colours = toneOf(prompt.tone)
        return (
          <Box
            key={prompt.key}
            sx={{
              p: 1.25,
              borderRadius: 2,
              bgcolor: prompt.tone === 'red' ? colours.main : colours.bg,
              color: prompt.tone === 'red' ? '#fff' : colours.dark,
              border: 2,
              borderColor: colours.main,
              fontWeight: 700,
            }}
          >
            {prompt.text}
          </Box>
        )
      })}
      {clocks.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {clocks.map((clock) => {
            const colours = toneOf(clock.tone)
            return (
              <Box
                key={clock.key}
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  border: 2,
                  borderColor: colours.main,
                  bgcolor: clock.warn ? colours.main : colours.bg,
                  color: clock.warn ? '#fff' : colours.dark,
                  animation: clock.warn
                    ? `${pulse} 1.5s ease-in-out infinite`
                    : undefined,
                }}
              >
                <Typography variant="caption" fontWeight={700} display="block">
                  {clock.label}
                  {clock.count !== undefined ? ` (${clock.count})` : ''}
                </Typography>
                <Typography
                  fontFamily="monospace"
                  fontWeight={800}
                  fontSize="1.3rem"
                  lineHeight={1.1}
                >
                  {formatClock(clock.seconds)}
                </Typography>
              </Box>
            )
          })}
        </Stack>
      )}
    </Stack>
  )
}

export default ConsoleStatus
